import React, { createContext, ReactNode, useState, useEffect } from 'react';
import { User } from '@domain/account/models/User';
import { useAuthStore } from '../store/AuthStore';
import { useTutorialStore } from '../store/TutorialStore';
import { container } from '@infrastructure/container';
import { Company } from '@domain/account/models/Company';
import { clearImpersonationTarget, getImpersonationTargetUserId } from '../auth/impersonation';
import { establishSessionForCurrentAppwriteUser } from '../auth/establishSession';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: User | undefined;
  company: Company | undefined;
  isLoading: boolean;
  error: Error | null;
  login: (user: User, jwt: string, company?: Company) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const authStore = useAuthStore();
  const { resetTutorial } = useTutorialStore();

  const clearAuthState = async () => {
    clearImpersonationTarget();
    await authStore.clearAuth();
    setUser(undefined);
    setCompany(undefined);
    setIsAuthenticated(false);
    resetTutorial();
    setError(null);
  };

  const login = async (userData: User, jwt: string, companyData?: Company) => {
    setUser(userData);
    setCompany(companyData);
    setIsAuthenticated(true);
    setError(null);
    authStore.setAuth(userData.id, jwt, companyData?.id);

    if (companyData) {
      useTutorialStore.getState().hydrateFromCompany(companyData);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);

        const impersonatingOnLoad = Boolean(getImpersonationTargetUserId());

        if (!impersonatingOnLoad) {
          const session = await container.authRepository.getCurrentSession();
          if (session.status === 'error' || !session.data) {
            await clearAuthState();
            return;
          }
        }

        const userResponse = await container.authRepository.getCurrentUser();
        if (userResponse.status === 'error' || !userResponse.data) {
          await clearAuthState();
          return;
        }

        if (impersonatingOnLoad) {
          await establishSessionForCurrentAppwriteUser(login);
          return;
        }

        const jwt = useAuthStore.getState().userId !== userResponse.data.id
          ? await useAuthStore.getState().refreshJwt()
          : await useAuthStore.getState().getJwt();

        if (!jwt) {
          await clearAuthState();
          return;
        }

        const companyId = useAuthStore.getState().companyId;
        if (!companyId) {
          const companiesResponse = await container.companyRepository.getCompanies();
          if (
            companiesResponse.status === 'success'
            && companiesResponse.data?.length === 1
          ) {
            await login(userResponse.data, jwt, companiesResponse.data[0]);
            return;
          }
          if (
            companiesResponse.status === 'success'
            && companiesResponse.data
            && companiesResponse.data.length > 1
          ) {
            authStore.setAuth(userResponse.data.id, jwt);
            setUser(userResponse.data);
            return;
          }
          await clearAuthState();
          return;
        }

        const currentCompany = await container.companyRepository.getCompany(companyId);
        if (currentCompany.status === 'error' || !currentCompany.data) {
          await clearAuthState();
          return;
        }

        await login(userResponse.data, jwt, currentCompany.data);
      } catch (err) {
        console.error('Auth initialization error:', err);
        await clearAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const logout = async () => {
    if (isLoggingOut) {
      return;
    }

    try {
      setIsLoggingOut(true);
      clearImpersonationTarget();
      await authStore.clearAuth();
      await container.authRepository.logout();
      setUser(undefined);
      setCompany(undefined);
      setIsAuthenticated(false);
      resetTutorial();
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err as Error);
    } finally {
      setIsLoggingOut(false);
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        company,
        isLoading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
